import { useQuery } from '@tanstack/react-query';
import { useApi } from '../services/useApi';
import { ApiSuccessResponse } from '../types/api';

export interface CommonOption {
  value: string;
  label: string;
}

interface CommonOptions {
  currencies: CommonOption[];
  paymentTerms: CommonOption[];
  vatZones: CommonOption[];
  customerGroups: CommonOption[];
  supplierGroups: CommonOption[];
  units: CommonOption[];
  departmentalDistributions: CommonOption[];
  productGroups: CommonOption[];
  journalResponse: CommonOption[];
  entryTypeOptions: CommonOption[];
  accountTypeOptions: CommonOption[];
  keyFigureCodes: CommonOption[];
  vatCodes: CommonOption[];
  yesNoOptions: CommonOption[];
}

interface CurrencyResponse {
  code: string;
  name: string;
}

interface PaymentTermResponse {
  name: string;
  paymentTermsNumber: string;
}

interface VatZoneResponse {
  vatZoneNumber: string;
  name: string;
}

interface CustomerGroupResponse {
  customerGroupNumber: string;
  name: string;
}

interface SupplierGroupResponse {
  supplierGroupNumber: string;
  name: string;
}

interface UnitResponse {
  unitNumber: string;
  name: string;
}

interface DepartmentalDistributionResponse {
  departmentalDistributionNumber: string;
  name: string;
}

interface ProductGroupResponse {
  productGroupNumber: string;
  name: string;
}

interface JournalResponse {
  journalNumber: string;
  name: string;
}

interface AccountTypeResponse {
  type: string;
  name: string;
}

interface VatCodeResponse {
  vatCode: string;
  name: string;
}

interface YesNoResponse {
  value: string;
  label: string;
}

interface KeyFigureCodeResponse {
  number: string;
  name: string;
}

export const useCommonOptions = () => {
  const api = useApi();

  const fetchCommonOptions = async (): Promise<CommonOptions> => {
  
    const [currenciesResponse, paymentTermsResponse, vatZonesResponse,customerGroupsResponse, supplierGroupsResponse, unitsResponse, departmentalDistributionsResponse, productGroupsResponse,journalResponse,vatCodesResponse,keyFigureCodesResponse ] = await Promise.all([
      api.get<ApiSuccessResponse<CurrencyResponse[]>>('/main/common-options?act=currencies'),
      api.get<ApiSuccessResponse<PaymentTermResponse[]>>('/main/common-options?act=payment-terms'),
      api.get<ApiSuccessResponse<VatZoneResponse[]>>('/main/common-options?act=vat-zones'),
      api.get<ApiSuccessResponse<CustomerGroupResponse[]>>('/main/common-options?act=customer-groups'),
      api.get<ApiSuccessResponse<SupplierGroupResponse[]>>('/main/common-options?act=supplier-groups'),
      api.get<ApiSuccessResponse<UnitResponse[]>>('/main/common-options?act=units'),
      api.get<ApiSuccessResponse<DepartmentalDistributionResponse[]>>('/main/common-options?act=departmental-distributions'),
      api.get<ApiSuccessResponse<ProductGroupResponse[]>>('/main/common-options?act=product-groups'),
      api.get<ApiSuccessResponse<JournalResponse[]>>('/main/common-options?act=journals'),
      api.get<ApiSuccessResponse<VatCodeResponse[]>>('/main/common-options?act=vat-accounts'),
      api.get<ApiSuccessResponse<KeyFigureCodeResponse[]>>('/main/common-options?act=key-figure-codes')
    ]);
    return {
      currencies: currenciesResponse.data.data.map((item) => ({
        value: item.code,
        label: item.code
      })),
      paymentTerms: paymentTermsResponse.data.data.map((item) => ({
        value: item.paymentTermsNumber,
        label: item.name
      })),
      vatZones: vatZonesResponse.data.data.map((item) => ({
        value: item.vatZoneNumber,
        label: item.name
      })),
      customerGroups: customerGroupsResponse.data.data.map((item) => ({
        value: item.customerGroupNumber,
        label: item.name
      })),
      supplierGroups: supplierGroupsResponse.data.data.map((item) => ({
        value: item.supplierGroupNumber,
        label: item.name
      })),
      units: unitsResponse.data.data.map((item) => ({
        value: item.unitNumber,
        label: item.name
      })),
      departmentalDistributions: departmentalDistributionsResponse.data.data.map((item) => ({
        value: item.departmentalDistributionNumber,
        label: item.name
      })),
      productGroups: productGroupsResponse.data.data.map((item) => ({
        value: item.productGroupNumber,
        label: item.name
      })),
      journalResponse: journalResponse.data.data.map((item) => ({
        value: item.journalNumber,
        label: item.name
      })),
      keyFigureCodes: keyFigureCodesResponse.data.data.map((item) => ({
        value: item.number,
        label: item.name
      })),
      entryTypeOptions: [
        { value: '2', label: 'Customer Payment' },
        { value: '3', label: 'Supplier Invoice' },
        { value: '4', label: 'Supplier Payment' },
        { value: '5', label: 'Finance Voucher' },
        { value: '10', label: 'Manual Customer Invoice' },
      ],
      accountTypeOptions: [
        { value: '1', label: 'Profit and Loss' },
        { value: '2', label: 'Status' },
        { value: '3', label: 'TotalFrom' },
        { value: '4', label: 'Heading' }, 
        { value: '5', label: 'Heading Start' },
        { value: '6', label: 'Sum Interval' },
        { value: '7', label: 'Sum Alpha' },
      ],
      vatCodes: vatCodesResponse.data.data.map((item) => ({
        value: item.vatCode,
        label: item.vatCode + " - " + item.name
      })),
      yesNoOptions: [
        { value: '1', label: 'Yes' },
        { value: '0', label: 'No' },
      ]
    };
  };
  const { data, isLoading, error } = useQuery(['commonOptions'], fetchCommonOptions, {
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    cacheTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  });

  return {
    currencies: data?.currencies || [],
    paymentTerms: data?.paymentTerms || [],
    vatZones: data?.vatZones || [],
    customerGroups: data?.customerGroups || [],
    supplierGroups: data?.supplierGroups || [],
    units: data?.units || [],
    departmentalDistributions: data?.departmentalDistributions || [],
    productGroups: data?.productGroups || [],
    journals: data?.journalResponse || [],
    entryTypeOptions: data?.entryTypeOptions || [],
    accountTypeOptions: data?.accountTypeOptions || [],
    vatCodes: data?.vatCodes || [],
    yesNoOptions: data?.yesNoOptions || [],
    keyFigureCodes: data?.keyFigureCodes || [],
    isLoading,
    error
  };
}; 